import { HttpClient } from '@angular/common/http';
import * as i0 from '@angular/core';
import { inject, Injectable, DestroyRef, output, input, Directive } from '@angular/core';
import { ADDRESS_WEB_SERVICE, CONTEXT_SESSION_TOKEN_FALSE } from '@effy-tech/angular-common/web-service';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import { tap, debounceTime, distinctUntilChanged, filter, switchMap, catchError, of, map as map$1 } from 'rxjs';

// ADDRESS PARAMS
const ADDRESS_WS_PARAM_TYPE = 'housenumber';
const ADDRESS_WS_PARAM_AUTOCOMPLETE = '1';
class AddressService {
    constructor() {
        this.httpClient = inject(HttpClient);
        this.addressWebService = inject(ADDRESS_WEB_SERVICE);
    }
    mapAdressesList(addresses) {
        return addresses.map((address) => ({ ...address.properties, ...address.geometry }));
    }
    search(value) {
        return this.httpClient.get(this.addressWebService.search, {
            params: {
                q: value,
                type: ADDRESS_WS_PARAM_TYPE,
            },
            context: CONTEXT_SESSION_TOKEN_FALSE,
        });
    }
    getAutocompleteList(value) {
        return this.search(value).pipe(map(response => this.mapAdressesList(response.features)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.13", ngImport: i0, type: AddressService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.13", ngImport: i0, type: AddressService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.13", ngImport: i0, type: AddressService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class AddressUtils {
    static isValid(address) {
        return (!!address.city &&
            AddressUtils.isValidLongitude(AddressUtils.getLongitude(address)) &&
            AddressUtils.isValidLatitude(AddressUtils.getLatitude(address)) &&
            !!address.streetNumberAndName &&
            !!address.postalCode);
    }
    static getLongitude(address) {
        return address.coordinates[0];
    }
    static getLatitude(address) {
        return address.coordinates[1];
    }
    static isValidLongitude(longitude) {
        return isFinite(longitude) && Math.abs(longitude) <= 180;
    }
    static isValidLatitude(latitude) {
        return isFinite(latitude) && Math.abs(latitude) <= 90;
    }
    static mapAddressCityToAddress(address) {
        return {
            city: address.city,
            coordinates: address.coordinates,
            streetNumberAndName: address.name,
            postalCode: address.postcode,
            cityCode: address.citycode,
        };
    }
    static formatAddress(addressLine, city, postalCode) {
        if (!addressLine || !city || !postalCode) {
            return '';
        }
        return `${addressLine}, ${postalCode} ${city}`;
    }
}

class AutocompleteAddressDirective {
    constructor() {
        this.ngControl = inject(NgControl);
        this.addressService = inject(AddressService);
        this.destroy$ = inject(DestroyRef);
        this.results = output();
        this.loading = output();
        this.complete = output();
        this.debounceTime = input(450);
        this.searchMinLength = input(5);
    }
    ngOnInit() {
        if (!this.ngControl?.control) {
            console.warn('[effyAutocompleteAddress] No FormControl found');
            return;
        }
        this.ngControl.control.valueChanges
            .pipe(tap(() => this.complete.emit(false)), debounceTime(this.debounceTime()), distinctUntilChanged(), filter((search) => search?.length > this.searchMinLength()), tap(() => this.loading.emit(true)), switchMap((search) => this.addressService.getAutocompleteList(search).pipe(catchError(() => {
            this.ngControl.control?.setErrors({ serverError: true });
            return of([]);
        }))), tap(() => {
            this.loading.emit(false);
            this.complete.emit(true);
        }), map$1((addresses) => addresses.map((address) => AddressUtils.mapAddressCityToAddress(address))), takeUntilDestroyed(this.destroy$))
            .subscribe(results => this.results.emit(results));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.13", ngImport: i0, type: AutocompleteAddressDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.13", type: AutocompleteAddressDirective, isStandalone: true, selector: "[effyAutocompleteAddress]", inputs: { debounceTime: { classPropertyName: "debounceTime", publicName: "debounceTime", isSignal: true, isRequired: false, transformFunction: null }, searchMinLength: { classPropertyName: "searchMinLength", publicName: "searchMinLength", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { results: "results", loading: "loading", complete: "complete" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.13", ngImport: i0, type: AutocompleteAddressDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[effyAutocompleteAddress]',
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ADDRESS_WS_PARAM_AUTOCOMPLETE, ADDRESS_WS_PARAM_TYPE, AddressService, AddressUtils, AutocompleteAddressDirective };
